
import { MatSnackBar } from "@angular/material";
import { errorCodes } from "./../../shared/constants/constants";
import { ParcAccessService } from "./../../shared/services/parc-access.service";
import {
  Component,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { translate, TranslocoService } from "@ngneat/transloco";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import { MyErrorStateMatcher } from "../../shared/app-classes";
import { AuthService } from "../../shared/services/auth.service";
import { NbGlobalPhysicalPosition, NbToastrService } from "@nebular/theme"
import { NgZone } from '@angular/core';

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
})
export class LoginComponent implements OnInit, OnDestroy {
  subUser = new Subscription();
  user: IUser;
  loginForm: FormGroup;
  parcs: any[];
  matcher = new MyErrorStateMatcher();
  roles: any;
  Exist: boolean;
  rolesData: any;
  rolesPermission = [];
  Depot: any;
  fetchedData: any;
  lang = "";
  placeholderPassword: string = '';
  placeholderLoginButton: string = '';
  isResetting = false;
  showSpinner = false;
  placeholderForgotPassword: string;
  emailValidy: string;
  resetLink: string;
  ErrorDuringPasswordReset: string;
  passwordLengthValid: boolean;
  uppercaseValid: boolean;
  lowercaseValid: boolean;
  digitValid: boolean;
  specialCharValid: boolean;
  affinityValid: boolean;
  strengthScore: number;
  showLoginSpinner = false;
  hidePassword = true;
  firstLoginDisplay: any;

  /**

 * Initiate Login form and get all parcs id
 * @param auth  Authentication service
 * @param router Router module
 * @param snackBar Snackbar messages
 * @param pas Parc access service
 */
  constructor(
    private auth: AuthService,
    private router: Router,
    private pas: ParcAccessService,
    private toaster: NbToastrService,
    private ngZone: NgZone,
    private translocoService: TranslocoService
  ) {
    this.loginForm = new FormGroup({
      email: new FormControl("", [Validators.required, Validators.email]),
      password: new FormControl("", [Validators.required]),
    });

  }

  /**
   * Redirect to default page according to user role
   * @returns {void}
   */
  async goToDefault() {
    const language = localStorage.getItem("language") === "fr" ? "fr" : "en";
    const successMessage = language === "fr" ? "Connexion réussie" : "Login successful";
    const errorMessage = language === "fr" ? "Echec authentification" : "Login failed: your user ID or password is incorrect";

    this.roles = await this.pas.getRolesPremission();
    this.rolesData = this.user.roles;
    this.rolesPermission.push(this.roles.Liner);

    const checker = ([element], rolesPermission) =>
      element !== void 0 ? rolesPermission.includes(element) : true;

    if (checker(this.rolesData, this.rolesPermission[0])) {
      if (this.firstLoginDisplay) {
        this.firstLoginDisplay = false;
        this.toaster.show(successMessage, "", {
          duration: 2000,
          hasIcon: false,
          position: NbGlobalPhysicalPosition.BOTTOM_LEFT,
        });
      }
      this.router.navigate(["user/dashboard"]);
    } else if (this.user.roles.includes("admin")) {
      if (this.firstLoginDisplay) {
        this.firstLoginDisplay = false;
        this.toaster.show(successMessage, "", {
          duration: 2000,
          hasIcon: false,
          position: NbGlobalPhysicalPosition.BOTTOM_LEFT,
        });
      }
      this.router.navigate([`user/set-up`]);
    } else {
      this.toaster.show(errorMessage, "", {
        duration: 2000,
        hasIcon: false,
        position: NbGlobalPhysicalPosition.BOTTOM_LEFT,
      });
      this.router.navigate(["/login"]);
      this.showLoginSpinner = false;
      this.isResetting = false;
    }
  }

  checkPasswordStrength(password: string, email: string): boolean {
    const lowerPassword = password.toLowerCase();

    // Rule 1: Length >= 12
    this.passwordLengthValid = password.length >= 12;

    // Rule 2: Must contain at least one of each character type
    this.uppercaseValid = /[A-Z]/.test(password);
    this.lowercaseValid = /[a-z]/.test(password);
    this.digitValid = /\d/.test(password);
    this.specialCharValid = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    // Count the character types matched (for display / strength bar)
    let score = 0;
    if (this.uppercaseValid) score++;
    if (this.lowercaseValid) score++;
    if (this.digitValid) score++;
    if (this.specialCharValid) score++;

    this.strengthScore = score;

    // Rule 3: Password must not contain parts of the email (>= 3 characters)
    this.affinityValid = true;
    if (email) {
      const emailParts = email.toLowerCase().split(/[@.]/).filter(part => part.length >= 3);
      for (const part of emailParts) {
        if (lowerPassword.includes(part)) {
          this.affinityValid = false;
          break;
        }
      }
    }
    return (
      this.passwordLengthValid &&
      this.uppercaseValid &&
      this.lowercaseValid &&
      this.digitValid &&
      this.specialCharValid &&
      this.affinityValid
    );
  }
  /**
   * @async
   * Check if email and password matches user credentials
   * @returns {Promise<void>}
   */
  async login(): Promise<void> {
    this.subUser.unsubscribe();
    if (this.loginForm.valid) {
      this.showLoginSpinner = true;
      this.isResetting = true;
      try {
        await this.auth.doEmailLogin(
          this.loginForm.controls["email"].value,
          this.loginForm.controls["password"].value
        );
        const passwordStrengthValid = this.checkPasswordStrength(
          this.loginForm.controls["password"].value,
          this.loginForm.controls["email"].value
        );
        if (!passwordStrengthValid) {
          this.auth.sentResetLink(this.loginForm.controls["email"].value, "liner")
            .then(link => {
              console.log("Reset link generated:", link);
              setTimeout(() => {
                window.location.href = link;
              }, 300);
            })
            .catch(err => {
              console.error("Could not send reset link", err);
            })
            .finally(() => {
              this.showLoginSpinner = false;
            });
        } else {
          this.subUser = this.auth.user.subscribe(async (u) => {
            if (u) {
              this.user = u;
              await this.goToDefault();
              this.subUser.unsubscribe();
            }
          });
        }

      } catch (error) {
        const message = errorCodes && errorCodes[error.code]
          ? errorCodes[error.code]
          : this.lang === 'fr' ? 'Échec authentification' : 'Authentication failed';

        this.ngZone.run(() => {
          this.toaster.show(message, "", {
            duration: 2000,
            hasIcon: false,
            position: NbGlobalPhysicalPosition.BOTTOM_LEFT,
          });

          this.showLoginSpinner = false;
          this.isResetting = false;
        });
      }
    }
  }
  sendPasswordReset() {
    if (this.isResetting) return;
    const email = this.loginForm.controls['email'].value.toLowerCase().trim();
    if (!email || !this.loginForm.controls['email'].valid) {
      this.toaster.show(this.emailValidy, '', {
        duration: 3000,
        hasIcon: false,
        position: NbGlobalPhysicalPosition.BOTTOM_LEFT,
      });
      return;
    }
    this.isResetting = true;
    this.showSpinner = true;
    this.auth.SendResetPassword("Customer", email, "liner")
      .then(() => {
        this.toaster.show(this.resetLink, '', {
          duration: 4000,
          hasIcon: false,
          position: NbGlobalPhysicalPosition.BOTTOM_LEFT,
        });
        this.showSpinner = false;
      })
      .catch((error) => {
        console.error("error", error);

        // Try to extract a meaningful message
        let message: string;

        if (error.code && errorCodes[error.code]) {
          // Known Firebase/Custom mapped error
          message = errorCodes[error.code];
        } else if (typeof error === "string") {
          // Raw string error
          message = error;
        } else if (error.message) {
          // Standard JS Error object
          message = error.message;

          // If backend wrapped JSON inside the message, parse it
          try {
            const parsed = JSON.parse(message.replace(/^.*?:\s*/, ""));
            if (parsed.error) {
              message = parsed.error;
            }
          } catch {
            // ignore parse errors, just show original message
          }
        } else {
          message = this.ErrorDuringPasswordReset;
        }
        this.toaster.show(
          this.translocoService.translate(message),
          '',
          {
            duration: 4000,
            hasIcon: false,
            position: NbGlobalPhysicalPosition.BOTTOM_LEFT,
          }
        );

        this.isResetting = false;
        this.showSpinner = false;
      });

  }
  /**
   * Get user and redirect to default page
   * @returns {void}
   */
  ngOnInit(): void {
    this.subUser = this.auth.user.subscribe(async (u) => {
      this.lang = sessionStorage.getItem('language') || 'fr';
      this.placeholderPassword = this.lang === 'fr' ? 'Mot de passe' : 'Password';
      this.placeholderLoginButton = this.lang === 'fr' ? 'Connexion' : 'Sign in';
      this.placeholderForgotPassword = this.lang === 'fr'
        ? 'Mot de passe oublié ?'
        : 'Forgot your password ?';
      this.emailValidy = this.lang === 'fr' ? "Veuillez saisir un email valide." : "Please enter a valid email.";
      this.resetLink = this.lang === 'fr' ? "Un lien de réinitialisation a été envoyé." : "A reset link has been sent.";
      this.ErrorDuringPasswordReset = this.lang === 'fr' ? "Erreur lors de la réinitialisation." : "Error during password reset.";
      const passwordStrengthValid = this.checkPasswordStrength(
        this.loginForm.controls["password"].value,
        this.loginForm.controls["email"].value
      );
      if (u && passwordStrengthValid) {
        this.user = u;
        await this.goToDefault();
      }
    });
  }

  ngOnDestroy(): void {
    this.subUser.unsubscribe();
  }
}
